// Generated by GitHub Copilot
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CMD_CENTER_STORAGE_KEY } from '../../constants/cmdCenter';
import { DEFAULT_DASHBOARD, LEADERSHIP, SECTION_TABS } from './cmdcenter/defaults';
import { DEFAULT_NARRATION, NARRATION_LABELS, getChatResponse, buildGccData } from './cmdcenter/chatData';
import { mergeDashboard, brandCssVars, companyInitials } from './cmdcenter/helpers';
import { buildChartConfigs } from './cmdcenter/chartConfigs';
import { Chart } from './cmdcenter/Chart';
import styles from './CmdCenter.module.css';

/**
 * Command Center page — a native React port of public/pepsico_gcc_command_centre.html.
 *
 * Opened in a new tab by CommandCenterPopup (GCC Reinvention) once
 * POST /gcc-command-centre-v3 responds. The popup stores { command, response }
 * in localStorage; this page reads response.dashboardData, overlays it on the
 * template defaults, and renders the full dashboard.
 */

const cx = (...a) => a.filter(Boolean).join(' ');

// The dashboard is a 1:1 pixel port of the HTML template (13px base). To make it
// always open at the template's compact "100% zoom" size — regardless of the
// browser's remembered per-site zoom for this origin — we apply a fixed document
// zoom while the page is mounted. Tune this one number if it's not an exact match.
const DASHBOARD_ZOOM = 0.7;

const readHandedOff = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CMD_CENTER_STORAGE_KEY));
    return parsed?.response?.dashboardData ? parsed : null;
  } catch {
    return null;
  }
};

const trendClass = (dir) => (dir === 'up' ? styles.kpiUp : dir === 'down' ? styles.kpiDown : styles.kpiFlat);

// ── Small presentational pieces ──────────────────────────────
const Insight = ({ html, icon = '💡' }) => (
  <div className={styles.insight}>
    <div className={styles.insIcon}>{icon}</div>
    <div className={styles.insText} dangerouslySetInnerHTML={{ __html: html }} />
  </div>
);

const Callout = ({ c, style }) =>
  c ? (
    <div className={styles.callout} style={style}>
      <div className={styles.calloutTitle}>{c.title}</div>
      <div className={styles.calloutText}>{c.text}</div>
    </div>
  ) : null;

const KpiCard = ({ k, onTipEnter, onTipMove, onTipLeave }) => (
  <div
    className={styles.kpiCard}
    onMouseEnter={() => k.tip_title && onTipEnter(k)}
    onMouseMove={onTipMove}
    onMouseLeave={onTipLeave}
  >
    <div className={styles.kpiV}>
      {k.value}
      {k.unit ? <span className={styles.kpiUnit}>{k.unit}</span> : null}
    </div>
    <div className={styles.kpiL}>{k.label}</div>
    {k.trend ? <div className={cx(styles.kpiSub, trendClass(k.trend_dir))}>{k.trend}</div> : null}
    {k.bar_pct != null ? (
      <div className={styles.kpiBar}>
        <div className={styles.kpiBarFill} style={{ width: `${k.bar_pct}%` }} />
      </div>
    ) : null}
  </div>
);

const Gauge = ({ g }) => {
  const circ = 2 * Math.PI * 38;
  const offset = (circ * (1 - (g.pct || 0) / 100)).toFixed(2);
  const statusCls = g.status === 'scale' ? styles.gsScale : g.status === 'plan' ? styles.gsPlan : styles.gsLive;
  return (
    <div className={styles.gaugeCard}>
      <svg className={styles.gaugeSvg} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="38" fill="none" stroke="var(--neutral-100)" strokeWidth="9" />
        <circle
          cx="50" cy="50" r="38" fill="none" stroke={g.color || 'var(--p700)'} strokeWidth="9"
          strokeDasharray="238.76" strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 50 50)"
        />
        <text x="50" y="46" textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--p800)" fontFamily="Inter,sans-serif">{g.pct}%</text>
        <text x="50" y="60" textAnchor="middle" fontSize="9" fill="var(--neutral-500)" fontFamily="Inter,sans-serif">covered</text>
      </svg>
      <div className={styles.gaugeLabel}>{g.label}</div>
      <div className={styles.gaugeSub}>{g.target}</div>
      <span className={cx(styles.gaugeStatus, statusCls)}>{g.status_label || 'Live'}</span>
    </div>
  );
};

const MIG_STATUS = {
  live: [styles.pillLive, 'Live'],
  in_progress: [styles.pillProg, 'In Progress'],
  'in-progress': [styles.pillProg, 'In Progress'],
  planned: [styles.pillPlan, 'Planned'],
};
const AI_STATUS = {
  live: [styles.aiLive, 'LIVE'],
  pilot: [styles.aiPilot, 'PILOT'],
  planned: [styles.aiPlan, 'PLANNED'],
};
const SLA_COLORS = { green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)' };
const PROG_COLORS = { blue: '', red: styles.pfRed, green: styles.pfGreen, amber: styles.pfAmber };
const SEV = { high: styles.rsHigh, medium: styles.rsMed, med: styles.rsMed, low: styles.rsLow };
const HOR_LABEL = { near: styles.horNear, mid: styles.horMid, far: styles.horFar };
const HOR_BULLET = ['var(--p700)', 'var(--red)', 'var(--green)'];

const SecHeader = ({ title, sub, tag, badge }) => (
  <div className={styles.secHeader}>
    <div>
      <div className={styles.secTitle}>{title}</div>
      <div className={styles.secSub}>{sub}</div>
    </div>
    {tag ? <span className={cx(styles.secBadge, badge)}>{tag}</span> : null}
  </div>
);

const EmptyState = () => (
  <div className={styles.message}>
    <h1 className={styles.messageTitle}>No Command Center data</h1>
    <p className={styles.messageText}>
      Choose <strong>GCC Reinvention</strong> under Our Offerings and enter a client
      name to generate a Command Center.
    </p>
  </div>
);

// ── Dashboard (always has data) ──────────────────────────────
const Dashboard = ({ handed }) => {
  const dashboard = useMemo(
    () => mergeDashboard(DEFAULT_DASHBOARD, handed.response?.dashboardData),
    [handed]
  );
  const company = dashboard.company;
  const s = dashboard.sections;
  const cssVars = useMemo(() => brandCssVars(company.brand), [company.brand]);
  const initials = companyInitials(company.name);
  const configs = useMemo(() => buildChartConfigs(s), [s]);
  const narration = useMemo(() => ({ ...DEFAULT_NARRATION, ...(dashboard.narration || {}) }), [dashboard.narration]);
  const gccData = useMemo(() => buildGccData(dashboard), [dashboard]);

  const [currentSection, setCurrentSection] = useState('overview');
  const [activeAvatars, setActiveAvatars] = useState({});
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, tip: null });
  const [narrOpen, setNarrOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'bot', text: `Hi! I'm your ${company.gcc_name || company.name || 'GCC'} AI. Ask me anything — headcount, savings, AI initiatives, scope, risks, or strategic direction.` },
  ]);
  const [typing, setTyping] = useState(false);
  const [copied, setCopied] = useState(false);

  const rootRef = useRef(null);
  const sectionRefs = useRef({});
  const msgsRef = useRef(null);

  // Load the Inter webfont once (template uses it).
  useEffect(() => {
    const id = 'gcc-inter-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    document.title = `${company.gcc_name || company.name || 'GCC'} — Command Centre`;
  }, [company]);

  // Force the compact template size regardless of the browser's remembered zoom.
  // Restored on unmount so other SPA routes are unaffected.
  useEffect(() => {
    const el = document.documentElement;
    const prev = el.style.zoom;
    el.style.zoom = String(DASHBOARD_ZOOM);
    return () => { el.style.zoom = prev; };
  }, []);

  // Highlight the section nearest the top as the user scrolls.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setCurrentSection(e.target.id); }),
      { root: rootRef.current, rootMargin: '-110px 0px -55% 0px', threshold: 0 }
    );
    SECTION_TABS.forEach(({ id }) => { const el = sectionRefs.current[id]; if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, typing, chatOpen]);

  const goTo = useCallback((id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentSection(id);
  }, []);

  const onTipEnter = useCallback((tip) => setTooltip((t) => ({ ...t, show: true, tip })), []);
  const onTipMove = useCallback((e) => {
    const x = Math.min(e.clientX + 14, window.innerWidth - 280);
    const y = e.clientY - 10;
    setTooltip((t) => (t.show ? { ...t, x, y } : t));
  }, []);
  const onTipLeave = useCallback(() => setTooltip((t) => ({ ...t, show: false })), []);

  const sendChat = useCallback((text) => {
    const msg = (text ?? chatInput).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setChatInput('');
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { role: 'bot', text: getChatResponse(msg, gccData) }]);
    }, 650);
  }, [chatInput, gccData]);

  const copyNarration = useCallback(() => {
    navigator.clipboard?.writeText(narration[currentSection] || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [narration, currentSection]);

  const setSectionRef = (id) => (el) => { if (el) sectionRefs.current[id] = el; };
  const kpiProps = { onTipEnter, onTipMove, onTipLeave };

  return (
    <div className={styles.root} ref={rootRef} style={cssVars}>
      {/* ── SIDEBAR ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sbBrand}>
          <div className={styles.sbLogo}>
            <div className={styles.logoMark}>{initials}</div>
            <div>
              <div className={styles.sbName}>{company.gcc_name || company.name}</div>
              <div className={styles.sbSub}>{company.tagline}</div>
            </div>
          </div>
        </div>
        <nav className={styles.sbNav}>
          <div className={styles.navGroup}>Dashboard</div>
          {SECTION_TABS.map(({ id, nav, icon }) => (
            <button
              key={id}
              type="button"
              className={cx(styles.navLink, currentSection === id && styles.active)}
              onClick={() => goTo(id)}
            >
              <span className={styles.navIcon}>{icon}</span> {nav}
            </button>
          ))}
          <div className={styles.navGroup} style={{ marginTop: 6 }}>Quick Facts</div>
          <div className={styles.sbQuickstats}>
            {(company.quick_facts || []).map((f, i) => (
              <div className={styles.qsItem} key={i}>
                <span>{f.label}</span><span className={styles.qsVal}>{f.value}</span>
              </div>
            ))}
          </div>
        </nav>
        <div className={styles.sbFooter}>
          {company.footer}<br />{company.name} © {new Date().getFullYear()}
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main className={styles.main}>
        <div className={styles.topbar}>
          <div className={styles.tbTitle}>GCC Command Centre</div>
          <div className={styles.tbBreadcrumb}>
            {(company.breadcrumb || []).map((b, i, arr) => (
              <React.Fragment key={i}>
                <span>{b}</span>{i < arr.length - 1 ? <span>›</span> : null}
              </React.Fragment>
            ))}
          </div>
          <div className={styles.tbSpacer} />
          <div className={styles.livePill}><div className={styles.liveDot} /> {company.live_label}</div>
          <div className={styles.confBadge}>⚠ Confidential</div>
        </div>

        {/* STATS STRIP */}
        <div className={styles.statsStrip}>
          {(dashboard.stats_strip || []).map((st, i) => (
            <div className={styles.statTile} key={i}>
              <div className={styles.statV}>{st.value}{st.sup ? <sup>{st.sup}</sup> : null}</div>
              <div className={styles.statL}>{st.label}</div>
              <div className={cx(styles.statNote, st.note_style === 'red' ? styles.snRed : st.note_style === 'blue' ? styles.snBlue : styles.snWhite)}>{st.note}</div>
            </div>
          ))}
        </div>

        {/* SECTION TABS */}
        <div className={styles.sectionNav}>
          {SECTION_TABS.map(({ id, label }) => (
            <button key={id} type="button" className={cx(styles.sectionTab, currentSection === id && styles.active)} onClick={() => goTo(id)}>
              {label}
            </button>
          ))}
        </div>

        <div className={styles.content}>
          {/* ── OVERVIEW ── */}
          <section className={styles.sectionBlock} id="overview" ref={setSectionRef('overview')}>
            <SecHeader title="GCC Overview" sub={s.overview.sub} tag={s.overview.tag} badge={styles.sbBlue} />
            <div className={styles.lsStrip}>
              <div className={styles.lsLabel}>Function Leadership</div>
              <div className={styles.lsAvatars}>
                {LEADERSHIP.map((p, i) => (
                  <div key={i} className={cx(styles.lsAv, activeAvatars[i] && styles.active)} onClick={() => setActiveAvatars((a) => ({ ...a, [i]: !a[i] }))}>
                    <div className={styles.avCircle} style={{ background: p.color }}>{p.initials}</div>
                    <div className={styles.avName}>{p.name}</div>
                    <div className={styles.avFn}>{p.fn}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Function Headcount Mix <span className={cx(styles.cardBadge, styles.cbAmber)}>〜 Illustrative</span></div>
                {(s.overview.func_mix || []).map((f, i) => (
                  <div className={styles.funcItem} key={i}>
                    <div className={styles.funcDot} style={{ background: f.color }} />
                    <div className={styles.funcName}>{f.label}</div>
                    <div className={styles.funcHc}>{f.hc}</div>
                    <div className={styles.funcPct}>{f.pct}</div>
                  </div>
                ))}
                <Insight html={s.overview.insight} />
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>GCC Growth Trajectory <span className={cx(styles.cardBadge, styles.cbBlue)}>✅ Public</span></div>
                <div className={styles.chartWrap}><Chart config={configs.growth} /></div>
                <div className={styles.srcTag}>{s.overview.growth_source}</div>
              </div>
            </div>
          </section>

          {/* ── SCOPE ── */}
          <section className={styles.sectionBlock} id="scope" ref={setSectionRef('scope')}>
            <SecHeader title="Scope Coverage" sub={s.scope.sub} tag={s.scope.tag} badge={styles.sbAmber} />
            <div className={styles.gaugeGrid}>
              {(s.scope.gauges || []).map((g, i) => <Gauge g={g} key={i} />)}
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Migration Status <span className={cx(styles.cardBadge, styles.cbAmber)}>〜 Illustrative</span></div>
                <table className={styles.migTable}>
                  <thead><tr><th>Process</th><th>Function</th><th>FTEs</th><th>Status</th></tr></thead>
                  <tbody>
                    {(s.scope.migration_table || []).map((r, i) => {
                      const [cls, lbl] = MIG_STATUS[r.status] || MIG_STATUS.planned;
                      return (
                        <tr key={i}>
                          <td>{r.process}</td><td>{r.function}</td><td>{r.ftes}</td>
                          <td><span className={cx(styles.pill, cls)}>{lbl}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Process Pipeline Overview</div>
                <div className={styles.chartWrapSm}><Chart config={configs.migration} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 14 }}>
                  {(s.scope.pipeline_counts || []).map((c, i) => (
                    <div style={{ textAlign: 'center' }} key={i}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: c.color, margin: '0 auto 4px' }} />
                      <div style={{ fontSize: 16, fontWeight: 800 }}>{c.value}</div>
                      <div style={{ fontSize: 9, color: 'var(--neutral-500)' }}>{c.label}</div>
                    </div>
                  ))}
                </div>
                <Callout c={s.scope.callout} />
              </div>
            </div>
          </section>

          {/* ── TALENT ── */}
          <section className={styles.sectionBlock} id="talent" ref={setSectionRef('talent')}>
            <SecHeader title="Talent & Workforce" sub={s.talent.sub} tag={s.talent.tag} badge={styles.sbAmber} />
            <div className={styles.kpiGrid}>
              {(s.talent.kpis || []).map((k, i) => <KpiCard k={k} key={i} {...kpiProps} />)}
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Attrition Trend vs Benchmark</div>
                <div className={styles.chartWrap}><Chart config={configs.attrition} /></div>
                <Insight html={s.talent.attrition_insight} />
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Workforce Composition</div>
                <div className={styles.chartWrapSm}><Chart config={configs.funcMix} /></div>
                <div className={styles.divider} />
                <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
                  {(s.talent.summary_stats || []).map((st, i) => (
                    <div key={i}>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{st.value}</div>
                      <div style={{ fontSize: 10, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 2 }}>{st.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── DELIVERY ── */}
          <section className={styles.sectionBlock} id="delivery" ref={setSectionRef('delivery')}>
            <SecHeader title="Delivery Performance" sub={s.delivery.sub} tag={s.delivery.tag} badge={styles.sbAmber} />
            <div className={styles.kpiGrid}>
              {(s.delivery.kpis || []).map((k, i) => <KpiCard k={k} key={i} {...kpiProps} />)}
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>SLA Performance by Service Line</div>
                {(s.delivery.sla_rows || []).map((r, i) => {
                  const col = SLA_COLORS[(r.color || 'green').toLowerCase()] || 'var(--green)';
                  return (
                    <div className={styles.slaRow} key={i}>
                      <div className={styles.slaName}>{r.name}</div>
                      <div className={styles.slaTrack}><div className={styles.slaFill} style={{ width: `${r.pct}%`, background: col }} /></div>
                      <div className={styles.slaPct} style={{ color: col }}>{r.pct}%</div>
                      <div className={styles.slaTgt}>{r.target}</div>
                    </div>
                  );
                })}
                <Insight html={s.delivery.sla_insight} icon="⚠️" />
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Platform Incidents (P1+P2) — Trend</div>
                <div className={styles.chartWrap}><Chart config={configs.incident} /></div>
                <div className={styles.srcTag}>{s.delivery.incident_note}</div>
              </div>
            </div>
          </section>

          {/* ── COST ── */}
          <section className={styles.sectionBlock} id="cost" ref={setSectionRef('cost')}>
            <SecHeader title="Cost & Value" sub={s.cost.sub} tag={s.cost.tag} badge={styles.sbAmber} />
            <div className={styles.kpiGrid}>
              {(s.cost.kpis || []).map((k, i) => <KpiCard k={k} key={i} {...kpiProps} />)}
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>OpEx Breakdown <span className={cx(styles.cardBadge, styles.cbAmber)}>〜 Estimates</span></div>
                <div className={styles.chartWrap}><Chart config={configs.opex} /></div>
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Annual Savings Ramp ($M) <span className={cx(styles.cardBadge, styles.cbAmber)}>〜 Estimates</span></div>
                <div className={styles.chartWrap}><Chart config={configs.savings} /></div>
                <Insight html={s.cost.savings_insight} />
              </div>
            </div>
          </section>

          {/* ── VALUE ── */}
          <section className={styles.sectionBlock} id="value" ref={setSectionRef('value')}>
            <SecHeader title="Value Realisation" sub={s.value.sub} tag={s.value.tag} badge={styles.sbAmber} />
            <div className={styles.kpiGrid}>
              {(s.value.kpis || []).map((k, i) => <KpiCard k={k} key={i} {...kpiProps} />)}
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Value Waterfall ($M) <span className={cx(styles.cardBadge, styles.cbAmber)}>〜 Estimates</span></div>
              <div className={styles.wfGrid}>
                {(s.value.value_waterfall || []).map((w, i, arr) => {
                  const isTotal = i === arr.length - 1;
                  return (
                    <div className={styles.wfBar} key={i}>
                      <div className={styles.wfV} style={isTotal ? undefined : { color: 'var(--green)' }}>{w.value_str || w.value}</div>
                      <div className={styles.wfBlock} style={{ background: w.color || 'var(--p700)' }}>{w.label}</div>
                      <div className={styles.wfL}>{w.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)} style={{ marginTop: 18 }}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>Value Category Mix</div>
                <div className={styles.chartWrapSm}><Chart config={configs.valueMix} /></div>
                <Callout c={s.value.value_mix_callout} />
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>BC vs Actuals — Cumulative ($M)</div>
                <div className={styles.chartWrapSm}><Chart config={configs.bc} /></div>
                <div className={styles.srcTag}>{s.value.bc_note}</div>
              </div>
            </div>
          </section>

          {/* ── DIGITAL ── */}
          <section className={styles.sectionBlock} id="digital" ref={setSectionRef('digital')}>
            <SecHeader title="Digital & AI" sub={s.digital.sub} tag={s.digital.tag} badge={styles.sbBlue} />
            <div className={styles.kpiGrid}>
              {(s.digital.kpis || []).map((k, i) => <KpiCard k={k} key={i} {...kpiProps} />)}
            </div>
            <div className={cx(styles.cardGrid, styles.grid2)}>
              <div className={styles.card}>
                <div className={styles.cardTitle}>AI & Digital Use Case Pipeline</div>
                {(s.digital.ai_pipeline || []).map((c, i) => {
                  const [cls, lbl] = AI_STATUS[(c.status || 'live').toLowerCase()] || AI_STATUS.live;
                  return (
                    <div className={styles.aiItem} key={i}>
                      <div className={cx(styles.aiStatus, cls)}>{lbl}</div>
                      <div className={styles.aiName}>{c.name}</div>
                      <div>
                        <div className={styles.aiNote}>{c.note}</div>
                        <div className={styles.aiNoteSub}>{c.note_sub}</div>
                      </div>
                    </div>
                  );
                })}
                <Insight html={s.digital.ai_insight} />
              </div>
              <div className={styles.card}>
                <div className={styles.cardTitle}>AI Skilling Progress</div>
                <div className={styles.chartWrapSm}><Chart config={configs.skilling} /></div>
                <div style={{ marginTop: 14 }}>
                  {(s.digital.skilling_progress || []).map((r, i) => (
                    <div className={styles.progRow} key={i}>
                      <div className={styles.progMeta}><span>{r.label}</span><span className={styles.progVal}>{r.pct}%</span></div>
                      <div className={styles.progTrack}><div className={cx(styles.progFill, PROG_COLORS[(r.color || 'blue').toLowerCase()])} style={{ width: `${r.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
                <Callout c={s.digital.tech_stack_callout} style={{ marginTop: 14 }} />
              </div>
            </div>
          </section>

          {/* ── ROADMAP ── */}
          <section className={styles.sectionBlock} id="roadmap" ref={setSectionRef('roadmap')}>
            <SecHeader title="Strategic Roadmap" sub={s.roadmap.sub} tag={s.roadmap.tag} badge={styles.sbBlue} />
            <div className={styles.card} style={{ marginBottom: 18 }}>
              <div className={styles.cardTitle}>GCC Maturity Positioning</div>
              <div className={styles.maturityWrap}>
                {(s.roadmap.maturity_steps || []).map((m, i) => (
                  <div className={styles.matStage} key={i}>
                    <div className={styles.matBarWrap}>
                      <div className={cx(styles.matBar, m.status === 'done' && styles.done, m.status === 'active' && styles.active)} style={{ height: m.height, position: m.status === 'active' ? 'relative' : undefined }}>
                        {m.marker ? <div className={styles.matMarker}>{m.marker}</div> : null}
                      </div>
                    </div>
                    <div className={cx(styles.matLabel, m.status === 'active' && styles.active)}>{m.label}<br />{m.sublabel}</div>
                  </div>
                ))}
              </div>
              <Insight html={s.roadmap.maturity_insight} />
            </div>
            <div className={styles.horizonGrid} style={{ marginBottom: 18 }}>
              {(s.roadmap.horizon || []).map((h, i) => (
                <div className={styles.horizonCard} key={i}>
                  <div className={cx(styles.horLabel, HOR_LABEL[h.label_style] || styles.horNear)}>{h.label}</div>
                  {(h.items || []).map((item, j) => (
                    <div className={styles.horItem} key={j}>
                      <div className={styles.horBullet} style={{ background: HOR_BULLET[i % HOR_BULLET.length] }} />{item}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Key Risks & Mitigations</div>
              {(s.roadmap.key_risks || []).map((r, i) => (
                <div className={styles.riskItem} key={i}>
                  <div className={cx(styles.riskSev, SEV[(r.severity || 'med').toLowerCase()] || styles.rsMed)} />
                  <div className={styles.riskName}>{r.name}</div>
                  <div className={styles.riskMit}>{r.mitigation}</div>
                </div>
              ))}
            </div>
            <div className={styles.srcTag} style={{ marginTop: 8 }}>{s.roadmap.sources}</div>
          </section>
        </div>
      </main>

      {/* ── TOOLTIP ── */}
      <div
        className={cx(styles.tt, tooltip.show && tooltip.tip && styles.show)}
        style={{ left: tooltip.x, top: tooltip.y }}
      >
        {tooltip.tip ? (
          <>
            <div className={styles.ttTitle}>{tooltip.tip.tip_title}</div>
            {tooltip.tip.tip_desc ? <div style={{ marginTop: 2 }}>{tooltip.tip.tip_desc}</div> : null}
            {tooltip.tip.tip_formula ? <div className={styles.ttRow}><span className={styles.ttKey}>Formula:</span>{tooltip.tip.tip_formula}</div> : null}
            {tooltip.tip.tip_bench ? <div className={styles.ttRow}><span className={styles.ttKey}>Benchmark:</span>{tooltip.tip.tip_bench}</div> : null}
          </>
        ) : null}
      </div>

      {/* ── NARRATION ── */}
      <button type="button" className={styles.narrToggle} onClick={() => setNarrOpen((o) => !o)}>
        {narrOpen ? '✕ Close' : '📖 Narrate'}
      </button>
      <div className={cx(styles.narrDrawer, narrOpen && styles.open)}>
        <div className={styles.ndHead}>
          <div className={styles.ndTitle}>📖 Executive Narrator</div>
          <button type="button" className={styles.ndClose} onClick={() => setNarrOpen(false)}>✕</button>
        </div>
        <div className={styles.ndSecLabel}>{NARRATION_LABELS[currentSection]}</div>
        <div className={styles.ndBody}><div className={styles.ndNarrative}>{narration[currentSection]}</div></div>
        <div className={styles.ndActions}>
          <button type="button" className={styles.ndBtn} onClick={copyNarration}>{copied ? '✅ Copied!' : '📋 Copy narrative'}</button>
          <button type="button" className={styles.ndBtn} onClick={() => setChatOpen(true)}>💬 Ask GCC AI a follow-up</button>
        </div>
      </div>

      {/* ── CHAT ── */}
      <button type="button" className={styles.chatFab} onClick={() => setChatOpen(true)}>
        <div className={styles.fabDot} /> 💬 Ask GCC AI
      </button>
      <div className={cx(styles.chatPanel, chatOpen && styles.open)}>
        <div className={styles.cpHead}>
          <div className={styles.cpAv}>{initials}</div>
          <div className={styles.cpInfo}>
            <div className={styles.cpName}>{company.gcc_name || company.name} AI</div>
            <div className={styles.cpStatus}><div className={styles.liveDot} style={{ width: 5, height: 5, marginRight: 3 }} /> Online · Knows all dashboard data</div>
          </div>
          <button type="button" className={styles.cpClose} onClick={() => setChatOpen(false)}>✕</button>
        </div>
        <div className={styles.cpMsgs} ref={msgsRef}>
          {messages.map((m, i) => (
            <div className={cx(styles.msg, m.role === 'user' ? styles.user : styles.bot)} key={i}>
              <div className={cx(styles.msgAv, m.role === 'user' ? styles.user : styles.bot)}>{m.role === 'user' ? 'You' : initials[0]}</div>
              <div className={styles.bubble}>{m.text}</div>
            </div>
          ))}
          {typing ? (
            <div className={cx(styles.msg, styles.bot)}>
              <div className={cx(styles.msgAv, styles.bot)}>{initials[0]}</div>
              <div className={styles.typingIndicator}><div className={styles.typingDot} /><div className={styles.typingDot} /><div className={styles.typingDot} /></div>
            </div>
          ) : null}
        </div>
        <div className={styles.cpSuggestions}>
          {["What's our ROI?", 'AI programmes?', 'Attrition risk?', 'Scope gaps?', 'Roadmap?'].map((q) => (
            <div className={styles.sugChip} key={q} onClick={() => sendChat(q)}>{q}</div>
          ))}
        </div>
        <div className={styles.cpInputWrap}>
          <input
            className={styles.cpInput}
            value={chatInput}
            placeholder={`Ask about the ${company.gcc_name || company.name || 'GCC'}…`}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
          />
          <button type="button" className={styles.cpSend} onClick={() => sendChat()}>↑</button>
        </div>
      </div>
    </div>
  );
};

const CmdCenter = () => {
  // Read once at mount. We intentionally keep the localStorage entry (do NOT
  // remove it) so a page refresh still renders the dashboard, and so React's
  // StrictMode dev double-mount can't blank it. The popup overwrites the key on
  // every new generation, so there is no stale-data problem for the normal flow.
  const [handed] = useState(readHandedOff);
  return handed ? <Dashboard handed={handed} /> : <EmptyState />;
};

export default CmdCenter;
